/**
 * Builds the "Material Consumption Summary" as an A4-portrait PDF — one row
 * per distinct material name with its total used/condemned quantity across
 * the date range. jspdf + jspdf-autotable are dynamically imported so they
 * stay out of the main bundle (same convention as dailyLogPdf.ts).
 */
import type { MaterialsSummaryLine } from '../components/MaterialsSummaryDoc';

export interface MaterialsSummaryPdfInput {
  org?: { name?: string; subtitle?: string };
  logoDataUrl?: string;
  from: string;
  to: string;
  rows: MaterialsSummaryLine[];
}

const NAME = 'U. N. Mehta Institute of Cardiology & Research Centre';
const SUB = 'Affiliated to B. J. Medical College, Ahmedabad-380016, INDIA';

const dmy = (d: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : d;
};

export async function buildMaterialsSummaryPdf(input: MaterialsSummaryPdfInput): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const M = 14;
  const org = input.org ?? {};

  if (input.logoDataUrl) {
    try { doc.addImage(input.logoDataUrl, 'PNG', M, M, 20, 13); } catch { /* bad image */ }
  }
  doc.setTextColor(18, 58, 134);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
  doc.text(org.name ?? NAME, pageW / 2, M + 5, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  doc.text(`(${org.subtitle ?? SUB})`, pageW / 2, M + 10, { align: 'center' });
  doc.setDrawColor(18, 58, 134); doc.setLineWidth(0.5);
  doc.line(M, M + 13, pageW - M, M + 13);

  doc.setTextColor(17, 17, 17);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text('Material Consumption Summary', pageW / 2, M + 20, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`From: ${dmy(input.from)}      To: ${dmy(input.to)}      Materials: ${input.rows.length}`, pageW / 2, M + 25, { align: 'center' });

  const totalUsed = input.rows.reduce((s, r) => s + r.usedQty, 0);
  const totalCondemned = input.rows.reduce((s, r) => s + r.condemnedQty, 0);

  autoTable(doc, {
    head: [['Material', 'Qty Used', 'Qty Condemned']],
    body: [
      ...input.rows.map((r) => [r.item, r.usedQty ? String(r.usedQty) : '—', r.condemnedQty ? String(r.condemnedQty) : '—']),
      [{ content: 'Total', styles: { fontStyle: 'bold' } }, { content: String(totalUsed), styles: { fontStyle: 'bold' } }, { content: String(totalCondemned), styles: { fontStyle: 'bold' } }],
    ] as never,
    startY: M + 30,
    margin: { left: M, right: M, bottom: 20 },
    tableLineColor: [0, 0, 0],
    tableLineWidth: 0.2,
    styles: { fontSize: 9, cellPadding: 2.2, lineColor: [0, 0, 0], lineWidth: 0.15, textColor: [17, 17, 17], valign: 'middle' },
    headStyles: { fillColor: [223, 230, 242], textColor: [17, 17, 17], fontStyle: 'bold', lineWidth: 0.2, halign: 'center' },
    columnStyles: { 1: { cellWidth: 32, halign: 'center' }, 2: { cellWidth: 36, halign: 'center' } },
  });

  return doc.output('blob');
}
